import { Box, createListCollection, HStack } from '@chakra-ui/react';
import { CustomSelect } from '@components/ui/select/Select';
import CameraControl, { ControlState, GaugeStyleEnum } from '@components/video/cameraControl/CameraControl';
import CameraLadButtons from '@components/video/cameraControl/CameraLadButtons';
import CameraPerimButtons from '@components/video/cameraControl/CameraPerimButtons';
import CameraPerimReplay from '@components/video/cameraControl/CameraPerimReplay';
import { useAppSelector, useAppStore } from '@hooks/redux.hooks';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { selectSelectedCameraKey } from '@redux/global/global.selector';
import { selectReplayActiveCameraConfigurations } from '@redux/replay/replay.selectors';
import { selectAllActiveCamerasConfiguration } from '@redux/sensors/sensors.selectors';
import {
  selectAllReplayActivePerimCameras,
  selectCameraMonitoringSensorByUniqueKey,
} from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { getDefaultFlux } from '@utils/sensors/camera/camera.utils';
import { takeAndDownloadScreenshotFromPlayer } from '@utils/sensors/camera/screenshot.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { Property } from 'csstype';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useReducer } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';

import { TrackingStatusEnum, TrackingTypeEnum } from '@/types/sensor/sensors';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

type CameraControlWrapperState = {
  selectedLadCameraKey: string | null;
  selectedPerimeterCameraUniqueKey: string | null;
  selectedReplayLadFluxUniqueKey: string | null;
};

type OwnProps = {
  isDynamic?: boolean;
  replayMode?: boolean;
  defaultSelectedCameraKey?: string | null;
  uniqueKey: string;
};

export default function CameraControlWrapper({
  isDynamic,
  defaultSelectedCameraKey = null,
  replayMode = false,
  uniqueKey,
}: Readonly<OwnProps>) {
  const store = useAppStore();
  const { formatMessage } = useIntl();

  const allActiveLadCameras = useAppSelector(selectAllActiveCamerasConfiguration);
  const allReplayActiveLadCameras = useAppSelector(selectReplayActiveCameraConfigurations);

  const allReplayActivePerimCameras: PerimeterCamera[] = useAppSelector((state) =>
    selectAllReplayActivePerimCameras(state, true),
  );
  const ladCamerasList = replayMode ? allReplayActiveLadCameras : allActiveLadCameras;
  const periCamerasList = replayMode ? allReplayActivePerimCameras : [];

  const [selectedCamera, setSelectedCamera] = useReducer(
    (state: CameraControlWrapperState, newState: Partial<CameraControlWrapperState>): CameraControlWrapperState => ({
      ...state,
      ...newState,
    }),
    {
      selectedLadCameraKey: replayMode ? null : defaultSelectedCameraKey,
      selectedPerimeterCameraUniqueKey: null,
      selectedReplayLadFluxUniqueKey: null,
    },
  );

  const globalSelectedCameraKey = useAppSelector(selectSelectedCameraKey);
  useEffect(() => {
    if (isDynamic) {
      setSelectedCamera({ selectedLadCameraKey: globalSelectedCameraKey ?? defaultSelectedCameraKey });
    }
  }, [globalSelectedCameraKey, isDynamic, defaultSelectedCameraKey]);

  const playerId = `control-player-${uniqueKey}`;
  const downloadScreenshot = useCallback(
    (cameraName?: string) => {
      const situationTime = selectSituationTime(store.getState(), replayMode);
      takeAndDownloadScreenshotFromPlayer(playerId, situationTime, cameraName);
    },
    [store, replayMode, playerId],
  );

  const displayPeriButtons = !!selectedCamera.selectedPerimeterCameraUniqueKey;

  // Variables pour les caméras LAD uniquement et leurs boutons
  const monitoringSensor = useAppSelector(
    (state) => selectCameraMonitoringSensorByUniqueKey(state, replayMode, selectedCamera.selectedLadCameraKey),
    isEqual,
  );
  const cameraConfiguration = monitoringSensor?.configuration ?? null;
  const cameraStatus = monitoringSensor?.status ?? null;

  const [ladControlState, setLadControlState] = useReducer(
    (state: ControlState, newState: Partial<ControlState>): ControlState => ({
      ...state,
      ...newState,
    }),
    {
      pip: false,
      displayedFlux: null,
      pipFlux: [],
      displayCross: true,
      displayVirtualJoystick: !replayMode,
      previousSelectedCameraKey: selectedCamera.selectedLadCameraKey,
      iso: false,
      hybridFocus: false,
      polarity: false,
      whiteBalance: false,
      round: false,
      simpleRoundSpeed: null,
      gaugeStyle: GaugeStyleEnum.GAUGE,
      fullscreen: false,
      mwirFilters: false,
    },
  );

  useEffect(() => {
    if (cameraConfiguration !== null && ladControlState.displayedFlux === null) {
      const defaultFlux = getDefaultFlux(cameraConfiguration);
      setLadControlState({
        displayedFlux: defaultFlux,
        pipFlux: cameraConfiguration.subCameras.filter((sc) => sc.uniqueKey !== defaultFlux?.uniqueKey),
      });
    }
  }, [cameraConfiguration, cameraStatus, ladControlState.displayedFlux]);

  const displayedVideoStatus = cameraStatus?.videoStatuses.find(
    (vs) => vs.id === ladControlState.displayedFlux?.cameraId,
  );
  const useCamera = useCameraCommand({
    monitoringSensor,
    openedSubCameraId: displayedVideoStatus?.id,
  });

  const ladFluxList = ladCamerasList
    .filter((cam) => getSensorUniqueKey(cam) === selectedCamera.selectedLadCameraKey)
    .flatMap((cam) => cam.subCameras);

  const fullScreenProps = ladControlState.fullscreen
    ? {
        top: 0,
        left: 0,
        zIndex: 'overlay',
        position: 'fixed' as Property.Position,
      }
    : {};

  useHotkeys('esc', () => ladControlState.fullscreen && setLadControlState({ fullscreen: false }));

  useHotkeys('space', (event) => {
    event.preventDefault();
    if (selectedCamera.selectedLadCameraKey === null) {
      return;
    }
    const videoTrackingStateOfSelectedFlux =
      (
        ladControlState.displayedFlux &&
        cameraStatus?.trackingStatus.videoTrackingStatus.holopticsVideoTrackings[ladControlState.displayedFlux.cameraId]
      )?.status ?? TrackingStatusEnum.DISABLED;
    if (videoTrackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE) {
      useCamera.videoTracking(null, TrackingStatusEnum.DISABLED, null);
      return;
    }
    const isTracking =
      cameraStatus?.trackingStatus.globalTrackingState === TrackingStatusEnum.ACTIVE ||
      cameraStatus?.trackingStatus.globalTrackingState === TrackingStatusEnum.LOST;
    const sensorTrackingState =
      cameraStatus?.trackingStatus.sensorTrackingStatus.globalSensorTrackingState ?? TrackingStatusEnum.DISABLED;
    const isSensorTracking =
      isTracking &&
      cameraStatus.trackingStatus.globalTrackingType === TrackingTypeEnum.SENSOR &&
      sensorTrackingState === TrackingStatusEnum.ACTIVE;
    if (isSensorTracking) {
      useCamera.sensorTracking(null, false);
    }
  });

  return (
    <Box
      backgroundColor="neutral.black"
      minHeight={0}
      minWidth={0}
      width="100%"
      height="100%"
      key={uniqueKey}
      userSelect="none"
      {...fullScreenProps}
    >
      {selectedCamera.selectedPerimeterCameraUniqueKey !== null ? (
        <CameraPerimReplay
          selectedPerimeterCameraUniqueKey={selectedCamera.selectedPerimeterCameraUniqueKey}
          playerId={playerId}
        />
      ) : (
        <CameraControl
          replayMode={replayMode}
          selectedCameraKey={selectedCamera.selectedLadCameraKey}
          uniqueKey={uniqueKey}
          monitoringSensor={monitoringSensor}
          controlState={ladControlState}
          setControlState={setLadControlState}
          useCamera={useCamera}
          replayLadFlux={
            replayMode
              ? (ladFluxList.find((subCam) => subCam.uniqueKey === selectedCamera.selectedReplayLadFluxUniqueKey) ??
                null)
              : null
          }
        />
      )}
      <HStack position="absolute" bottom={3} gap={1} width="100%" height="52px" alignItems="center" paddingX={2}>
        <CustomSelect
          width={replayMode ? '400px' : '375px'}
          key={selectedCamera.selectedLadCameraKey ?? undefined}
          value={selectedCamera.selectedLadCameraKey ?? undefined}
          placeholder={formatMessage({ id: `cameras.selectLADCamera` })}
          collection={createListCollection({
            items: ladCamerasList.map((cam) => ({
              label: cam.name,
              value: getSensorUniqueKey(cam),
            })),
          })}
          noOptionMessage="cameras.noLADCamera"
          onChange={(value) => {
            const selectedReplayUniqueKey = getDefaultFlux(
              ladCamerasList.find((cam) => getSensorUniqueKey(cam) === value) ?? null,
            );
            setSelectedCamera({
              selectedLadCameraKey: value,
              selectedPerimeterCameraUniqueKey: null,
              selectedReplayLadFluxUniqueKey: selectedReplayUniqueKey ? selectedReplayUniqueKey.uniqueKey : undefined,
            });
          }}
        />

        {replayMode && (
          <>
            {selectedCamera.selectedLadCameraKey && (
              <CustomSelect
                width="400px"
                key={selectedCamera.selectedReplayLadFluxUniqueKey ?? undefined}
                value={selectedCamera.selectedReplayLadFluxUniqueKey ?? undefined}
                placeholder={formatMessage({ id: `cameras.selectReplayLADFlux` })}
                collection={createListCollection({
                  items: ladFluxList.map((subCam) => ({
                    label: subCam.name,
                    value: subCam.uniqueKey,
                  })),
                })}
                noOptionMessage="cameras.noLADFlux"
                onChange={(value) => {
                  setSelectedCamera({
                    selectedReplayLadFluxUniqueKey: value,
                  });
                }}
              />
            )}
            <CustomSelect
              width="400px"
              key={selectedCamera.selectedPerimeterCameraUniqueKey ?? undefined}
              value={selectedCamera.selectedPerimeterCameraUniqueKey ?? undefined}
              placeholder={formatMessage({ id: `cameras.selectPerimeterCamera` })}
              collection={createListCollection({
                items: periCamerasList.map((cam) => ({
                  label: cam.name,
                  value: getSensorUniqueKey(cam),
                })),
              })}
              noOptionMessage="cameras.noPerimCamera"
              onChange={(value) =>
                setSelectedCamera({
                  selectedLadCameraKey: null,
                  selectedPerimeterCameraUniqueKey: value,
                })
              }
            />
          </>
        )}

        {displayPeriButtons ? (
          <CameraPerimButtons
            selectedPerimeterCameraUniqueKey={selectedCamera.selectedPerimeterCameraUniqueKey}
            downloadScreenshot={downloadScreenshot}
          />
        ) : (
          <CameraLadButtons
            monitoringSensor={monitoringSensor}
            controlState={ladControlState}
            setControlState={setLadControlState}
            selectedCameraKey={selectedCamera.selectedLadCameraKey}
            useCamera={useCamera}
            uniqueKey={uniqueKey}
            replayMode={replayMode}
            downloadScreenshot={() => downloadScreenshot(monitoringSensor?.configuration.name)}
          />
        )}
      </HStack>
    </Box>
  );
}
